import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import DashboardLayout from "@/components/DashboardLayout";
import { PRINTER_FORMATS, stageMeta } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Building2, Printer, Languages, Link2, Monitor, Copy, Check, ListChecks, GripVertical, Plus, Trash2, AlarmClock, Image as ImageIcon, Receipt } from "lucide-react";
import { toast } from "sonner";

const MAX_STEPS = 12;

export default function Settings() {
  const { user, refresh } = useAuth();
  const { t, setLang } = useLang();
  const [form, setForm] = useState({
    name: user?.name || "", mill_name: user?.mill_name || "", phone: user?.phone || "",
    address: user?.address || "", printer_format: user?.printer_format || "80mm", language: user?.language || "en",
    stage_alert_hours: user?.stage_alert_hours ?? 24, logo: user?.logo || "",
    price_per_kg: user?.price_per_kg ?? "", invoice_currency: user?.invoice_currency || "EUR",
  });
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  // Processing steps editor
  const [steps, setSteps] = useState((user?.stages || []).map((s) => ({ ...s })));
  const [stepsBusy, setStepsBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const liveUrl = `${window.location.origin}/live-queue/${user?.slug}`;

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, price_per_kg: form.price_per_kg === "" ? 0 : parseFloat(form.price_per_kg) || 0 };
      await api.put("/settings", payload);
      await refresh();
      if (form.language) setLang(form.language);
      toast.success(t("settings_saved"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copy = () => { navigator.clipboard.writeText(liveUrl); setCopied(true); toast.success(t("link_copied")); setTimeout(() => setCopied(false), 2000); };

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 256;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL("image/png");
        if (dataUrl.length > 200000) { toast.error(t("logo_too_big")); return; }
        setForm((f) => ({ ...f, logo: dataUrl }));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const updateStep = (i, label) => setSteps((prev) => prev.map((s, idx) => (idx === i ? { ...s, label } : s)));
  const addStep = () => {
    if (steps.length >= MAX_STEPS) { toast.error(t("max_steps_reached")); return; }
    setSteps((prev) => [...prev, { id: "", label: "" }]);
  };
  const removeStep = (i) => setSteps((prev) => prev.filter((_, idx) => idx !== i));

  const onStepDrop = (i) => {
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); return; }
    setSteps((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(i, 0, moved);
      return next;
    });
    setDragIdx(null);
  };

  const saveSteps = async () => {
    const clean = steps.map((s) => ({ id: s.id || "", label: (s.label || "").trim() })).filter((s) => s.label);
    if (clean.length === 0) { toast.error(t("min_one_step")); return; }
    if (clean.length > MAX_STEPS) { toast.error(t("max_steps_reached")); return; }
    const lower = clean.map((s) => s.label.toLowerCase());
    const dup = lower.find((l, i) => lower.indexOf(l) !== i);
    if (dup) { toast.error(`${t("duplicate_step")}: ${clean[lower.indexOf(dup)].label}`); return; }
    setStepsBusy(true);
    try {
      const { data } = await api.put("/stages", { stages: clean });
      setSteps(data.map((s) => ({ ...s })));
      await refresh();
      toast.success(t("steps_saved"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setStepsBusy(false); }
  };

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl space-y-6" data-testid="settings-page">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t("settings_title")}</h1>
          <p className="mt-1 text-muted-foreground">{t("settings_sub")}</p>
        </div>

        {/* Mill profile */}
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold"><Building2 className="h-5 w-5 text-olive" /> {t("mill_profile")}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>{t("name")}</Label><Input data-testid="settings-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>{t("mill_name")}</Label><Input data-testid="settings-mill" value={form.mill_name} onChange={(e) => setForm({ ...form, mill_name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>{t("phone")}</Label><Input data-testid="settings-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>{t("address")}</Label><Input data-testid="settings-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          </div>
          <div className="mt-4 flex items-center gap-4">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-secondary/40">
              {form.logo ? <img src={form.logo} alt="logo" data-testid="logo-preview" className="h-full w-full object-contain" /> : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
            </div>
            <div className="space-y-1.5">
              <Label>{t("mill_logo")}</Label>
              <div className="flex items-center gap-2">
                <label data-testid="upload-logo-label" className="cursor-pointer rounded-lg bg-secondary px-3 py-1.5 text-sm font-medium transition-colors hover:bg-secondary/70">
                  {t("upload_logo")}
                  <input type="file" accept="image/*" className="hidden" data-testid="logo-input" onChange={onLogoFile} />
                </label>
                {form.logo && <Button size="sm" variant="ghost" data-testid="remove-logo-btn" onClick={() => setForm({ ...form, logo: "" })}>{t("remove_logo")}</Button>}
              </div>
              <p className="text-xs text-muted-foreground">{t("logo_hint")}</p>
            </div>
          </div>
        </div>

        {/* Preferences */}
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold"><Printer className="h-5 w-5 text-olive" /> {t("default_printer")}</h2>
            <Select value={form.printer_format} onValueChange={(v) => setForm({ ...form, printer_format: v })}>
              <SelectTrigger data-testid="settings-printer"><SelectValue /></SelectTrigger>
              <SelectContent>{PRINTER_FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold"><Languages className="h-5 w-5 text-olive" /> {t("ui_language")}</h2>
            <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
              <SelectTrigger data-testid="settings-language"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="sq">Shqip (Albanian)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold"><AlarmClock className="h-5 w-5 text-olive" /> {t("stage_alert")}</h2>
            <div className="space-y-1.5">
              <Label>{t("stage_alert_hours")}</Label>
              <Input data-testid="settings-alert-hours" type="number" min="0.5" step="0.5" value={form.stage_alert_hours} onChange={(e) => setForm({ ...form, stage_alert_hours: e.target.value })} />
              <p className="text-xs text-muted-foreground">{t("stage_alert_hint")}</p>
            </div>
          </div>
        </div>

        {/* Invoice pricing */}
        <div className="rounded-xl border border-border bg-card p-6" data-testid="invoice-pricing-card">
          <h2 className="mb-1 flex items-center gap-2 font-heading text-lg font-semibold"><Receipt className="h-5 w-5 text-olive" /> {t("invoice_pricing")}</h2>
          <p className="mb-4 text-sm text-muted-foreground">{t("invoice_pricing_sub")}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("price_per_kg")}</Label>
              <Input data-testid="settings-price-per-kg" type="number" min="0" step="0.01" value={form.price_per_kg}
                onChange={(e) => setForm({ ...form, price_per_kg: e.target.value })} placeholder="0.00" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("invoice_currency")}</Label>
              <Select value={form.invoice_currency} onValueChange={(v) => setForm({ ...form, invoice_currency: v })}>
                <SelectTrigger data-testid="settings-invoice-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="ALL">ALL (L)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <Button data-testid="save-settings-btn" onClick={save} disabled={busy} className="rounded-full bg-primary hover:bg-olive-hover">{busy ? t("loading") : t("save")}</Button>

        {/* Processing steps */}
        <div className="rounded-xl border border-border bg-card p-6" data-testid="steps-section">
          <h2 className="mb-1 flex items-center gap-2 font-heading text-lg font-semibold"><ListChecks className="h-5 w-5 text-olive" /> {t("proc_steps")}</h2>
          <p className="mb-4 text-sm text-muted-foreground">{t("proc_steps_sub")}</p>
          <div className="space-y-2">
            {steps.map((s, i) => {
              const meta = stageMeta(s.id, i);
              return (
                <div
                  key={i}
                  data-testid={`step-row-${i}`}
                  draggable
                  onDragStart={() => setDragIdx(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onStepDrop(i)}
                  onDragEnd={() => setDragIdx(null)}
                  className={`flex items-center gap-2 rounded-lg border border-border bg-secondary/40 p-2 transition-opacity ${dragIdx === i ? "opacity-40" : ""}`}
                >
                  <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-muted-foreground/50" />
                  <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: meta.color }} />
                  <Input
                    data-testid={`step-input-${i}`}
                    value={s.label}
                    placeholder={t("step_name")}
                    onChange={(e) => updateStep(i, e.target.value)}
                    className="h-9 flex-1"
                  />
                  <Button
                    data-testid={`remove-step-${i}`}
                    size="icon"
                    variant="ghost"
                    disabled={steps.length <= 1}
                    onClick={() => removeStep(i)}
                    className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button data-testid="add-step-btn" variant="outline" size="sm" onClick={addStep} disabled={steps.length >= MAX_STEPS} className="rounded-full">
              <Plus className="mr-1 h-4 w-4" /> {t("add_step")}
            </Button>
            <Button data-testid="save-steps-btn" size="sm" onClick={saveSteps} disabled={stepsBusy} className="rounded-full bg-primary hover:bg-olive-hover">
              {stepsBusy ? t("loading") : t("save_steps")}
            </Button>
            <span className="text-xs text-muted-foreground">{steps.length}/{MAX_STEPS}</span>
          </div>
        </div>

        {/* Public URLs */}
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold"><Link2 className="h-5 w-5 text-olive" /> {t("public_urls")}</h2>
          <p className="mb-2 text-sm text-muted-foreground">{t("live_queue_url")}</p>
          <div className="flex items-center gap-2 rounded-lg border border-border bg-secondary/50 p-2">
            <Monitor className="ml-1 h-4 w-4 shrink-0 text-muted-foreground" />
            <code data-testid="live-url" className="flex-1 truncate text-xs">{liveUrl}</code>
            <Button data-testid="copy-url-btn" size="sm" variant="outline" onClick={copy} className="shrink-0">
              {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">{t("your_slug")}: <span className="font-mono">{user?.slug}</span></p>
        </div>
      </div>
    </DashboardLayout>
  );
}

